import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Chip, EmptyState, Skeleton, Table, TBody, TD, TH, THead, TR } from '../components/ui'
import { PageHeader } from '../components/PageHeader'
import { useAudit } from '../api/hooks'
import { ApiError } from '../api/client'
import type { AuditEntry } from '../api/types'

const CATEGORIES = ['access', 'person', 'credential', 'location', 'system', 'operator', 'auth']

const SINCE_OPTIONS: { label: string; seconds: number | null }[] = [
  { label: '24h', seconds: 24 * 3600 },
  { label: '7d', seconds: 7 * 86400 },
  { label: '30d', seconds: 30 * 86400 },
  { label: 'All', seconds: null },
]

/** YYYY-MM-DD HH:MM:SS local time for the audit log. */
function stamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function categoryOf(action: string): string {
  const dot = action.indexOf('.')
  return dot > 0 ? action.slice(0, dot) : action
}

const categoryTones: Record<string, 'gold' | 'info' | 'ok' | 'warn' | 'muted'> = {
  access: 'gold',
  credential: 'info',
  auth: 'ok',
  system: 'warn',
  location: 'muted',
  person: 'muted',
  operator: 'muted',
}

/** Route recognizable target unique_ids to their detail pages. */
function targetLink(uid: string): string | null {
  if (uid.startsWith('op-')) return `/directory/${uid}`
  if (uid.startsWith('ot-')) return `/credentials/${uid}`
  if (uid.startsWith('os-')) return `/systems/${uid}`
  return null
}

/** Route an audit actor to the most useful page we have. */
function actorLink(entry: AuditEntry): string | null {
  if (entry.actor_kind === 'person' && entry.actor_unique_id) return `/directory/${entry.actor_unique_id}`
  if ((entry.actor_kind === 'user' || entry.actor_kind === 'service_account') && entry.actor_unique_id) {
    return '/operators'
  }
  return null
}

/** Best-effort human-readable actor label. */
function actorLabel(entry: AuditEntry): string {
  if (entry.actor_name) return entry.actor_name
  if (entry.actor_unique_id) return entry.actor_unique_id
  return 'system'
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export default function AuditPage() {
  const [category, setCategory] = useState('')
  const [actor, setActor] = useState('')
  const [sinceSeconds, setSinceSeconds] = useState<number | null>(null)
  const [sinceIso, setSinceIso] = useState<string | undefined>(undefined)
  const [expanded, setExpanded] = useState<string | null>(null)

  // Debounce the free-text filters so each keystroke does not fire a query.
  const debouncedCategory = useDebounced(category, 250)
  const debouncedActor = useDebounced(actor, 250)

  const filters = {
    category: debouncedCategory || undefined,
    actor: debouncedActor || undefined,
    since: sinceIso,
  }

  const pickSince = (seconds: number | null) => {
    setSinceSeconds(seconds)
    setSinceIso(seconds ? new Date(Date.now() - seconds * 1000).toISOString() : undefined)
  }

  const audit = useAudit(filters)
  const items = audit.data?.pages.flatMap((p) => p.items) ?? []
  const endpointMissing = audit.isError && audit.error instanceof ApiError && audit.error.status === 404

  const inputClass =
    'rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink placeholder:text-ostrel-ink-muted focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold'

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Immutable operator log" title="Audit trail" />

      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-ostrel-line bg-ostrel-card p-3.5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="audit-category" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Category
          </label>
          <input
            id="audit-category"
            list="audit-categories"
            placeholder="all"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className={`w-44 ${inputClass}`}
          />
          <datalist id="audit-categories">
            {CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="audit-actor" className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
            Actor
          </label>
          <input
            id="audit-actor"
            placeholder="ou-… or osa-…"
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            className={`w-44 ${inputClass}`}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">Since</span>
          <span className="flex gap-1.5">
            {SINCE_OPTIONS.map((o) => (
              <button
                key={o.label}
                onClick={() => pickSince(o.seconds)}
                className={`tnum rounded-sm border px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
                  sinceSeconds === o.seconds
                    ? 'border-ostrel-navy bg-ostrel-navy text-ostrel-light'
                    : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
                }`}
              >
                {o.label}
              </button>
            ))}
          </span>
        </div>
        <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
          {items.length}
          {audit.hasNextPage ? '+' : ''} entries
        </span>
      </div>

      {audit.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
          <Skeleton className="h-9" />
        </div>
      ) : endpointMissing ? (
        <EmptyState
          title="Audit endpoint not available yet"
          hint="The backend audit API is still being rolled out — check back shortly."
        />
      ) : audit.isError ? (
        <EmptyState title="Could not load audit trail" hint={audit.error.message} />
      ) : items.length === 0 ? (
        <EmptyState title="No audit entries" hint="Try widening the filters above." />
      ) : (
        <div className="rounded-sm border border-ostrel-line bg-ostrel-card">
          <Table>
            <THead>
              <TR>
                <TH>Timestamp</TH>
                <TH>Actor</TH>
                <TH>Action</TH>
                <TH>Target</TH>
                <TH>Category</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((entry) => {
                const category = categoryOf(entry.action)
                const link = entry.target_unique_id ? targetLink(entry.target_unique_id) : null
                const isOpen = expanded === entry.unique_id
                return (
                  <AuditRow
                    key={entry.unique_id}
                    entry={entry}
                    category={category}
                    link={link}
                    isOpen={isOpen}
                    onToggle={() => setExpanded(isOpen ? null : entry.unique_id)}
                  />
                )
              })}
            </TBody>
          </Table>
        </div>
      )}

      {audit.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="ghost" size="md" disabled={audit.isFetchingNextPage} onClick={() => audit.fetchNextPage()}>
            {audit.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      )}

      <p className="text-[11px] leading-relaxed text-ostrel-ink-muted">
        Audit entries are append-only and retained for 24 months. Operator actions, connector changes and remote
        unlocks are recorded with the customer scope of the token that performed them.
      </p>
    </div>
  )
}

function AuditRow({
  entry,
  category,
  link,
  isOpen,
  onToggle,
}: {
  entry: AuditEntry
  category: string
  link: string | null
  isOpen: boolean
  onToggle: () => void
}) {
  return (
    <>
      <TR
        onClick={onToggle}
        className={entry.diff ? 'cursor-pointer' : ''}
        title={entry.diff ? 'Click to inspect the diff' : undefined}
      >
        <TD>
          <span className="tnum font-mono text-xs text-ostrel-ink-muted">{stamp(entry.at)}</span>
        </TD>
        <TD>
          {(() => {
            const link = actorLink(entry)
            const label = actorLabel(entry)
            return link ? (
              <Link
                to={link}
                onClick={(e) => e.stopPropagation()}
                className="text-sm text-ostrel-ink hover:text-ostrel-accent"
                title={entry.actor_unique_id ?? undefined}
              >
                {label}
              </Link>
            ) : (
              <span className="text-sm text-ostrel-ink" title={entry.actor_unique_id ?? undefined}>
                {label}
              </span>
            )
          })()}
        </TD>
        <TD>
          <span className="tnum font-mono text-xs text-ostrel-ink">{entry.action}</span>
        </TD>
        <TD>
          {entry.target_unique_id ? (
            link ? (
              <Link
                to={link}
                onClick={(e) => e.stopPropagation()}
                className="tnum font-mono text-xs text-ostrel-gold hover:text-ostrel-accent"
              >
                {entry.target_unique_id}
              </Link>
            ) : (
              <span className="tnum font-mono text-xs text-ostrel-ink-muted">{entry.target_unique_id}</span>
            )
          ) : (
            <span className="text-xs text-ostrel-ink-muted">—</span>
          )}
        </TD>
        <TD>
          <Chip tone={categoryTones[category] ?? 'muted'} label={category} />
        </TD>
      </TR>
      {isOpen && entry.diff && (
        <tr>
          <td colSpan={5} className="bg-ostrel-sand/60 px-3 py-2">
            <pre className="tnum overflow-x-auto font-mono text-[11px] leading-relaxed text-ostrel-ink">
              {JSON.stringify(entry.diff, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  )
}
